/**
 * 周期日期工具：自定义范围跨度与 shell2-app `period-date.ts` 对齐（按日历月计）
 */
import moment from 'moment';

/** 自定义日期范围最大跨度（月），与移动端营业分析筛选一致 */
export const CUSTOM_DATE_RANGE_MAX_MONTHS = 12;

/**
 * 校验自定义日期范围：结束不早于开始，且跨度不超过 {@link CUSTOM_DATE_RANGE_MAX_MONTHS} 个月（仅比较日历日）。
 * @param startDate - 开始日期
 * @param endDate - 结束日期
 * @returns 是否合法（不弹 toast，由调用方提示）
 */
export function isCustomDateRangeValid(
    startDate: string | Date,
    endDate: string | Date
): boolean {
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).startOf('day');
    const orderValid = end.isSame(start, 'day') || end.isAfter(start);
    const maxEnd = start.clone().add(CUSTOM_DATE_RANGE_MAX_MONTHS, 'months');
    const withinSpan = !end.isAfter(maxEnd, 'day');
    return orderValid && withinSpan;
}

/**
 * 自定义范围：结束日不晚于开始日 + {@link CUSTOM_DATE_RANGE_MAX_MONTHS} 个月（按日历月）。
 * @param startDate - 开始日期
 */
export function getMaxCustomEndDate(startDate: Date): Date {
    return moment(startDate).add(CUSTOM_DATE_RANGE_MAX_MONTHS, 'months').toDate();
}

/**
 * 自定义范围：开始日不早于结束日 - {@link CUSTOM_DATE_RANGE_MAX_MONTHS} 个月。
 * @param endDate - 结束日期
 */
export function getMinCustomStartDate(endDate: Date): Date {
    return moment(endDate).subtract(CUSTOM_DATE_RANGE_MAX_MONTHS, 'months').toDate();
}

/**
 * 将结束日期限制在 [start, start+最大跨度] 内（仅比较日历日，保留结束日时分）。
 * @param startDate - 开始日期
 * @param endDate - 结束日期
 */
export function clampCustomEndDate(startDate: Date, endDate: Date): Date {
    const startM = moment(startDate).startOf('day');
    const endM = moment(endDate).startOf('day');
    const maxM = moment(getMaxCustomEndDate(startDate)).startOf('day');
    if (endM.isBefore(startM, 'day')) {
        return copyTime(startM.toDate(), endDate);
    }
    if (endM.isAfter(maxM, 'day')) {
        return copyTime(maxM.toDate(), endDate);
    }
    return endDate;
}

/**
 * 将开始日期限制在 [end-最大跨度, end] 内（仅比较日历日，保留开始日时分）。
 * @param startDate - 开始日期
 * @param endDate - 结束日期
 */
export function clampCustomStartDate(startDate: Date, endDate: Date): Date {
    const startM = moment(startDate).startOf('day');
    const endM = moment(endDate).startOf('day');
    const minM = moment(getMinCustomStartDate(endDate)).startOf('day');
    if (startM.isAfter(endM, 'day')) {
        return copyTime(endM.toDate(), startDate);
    }
    if (startM.isBefore(minM, 'day')) {
        return copyTime(minM.toDate(), startDate);
    }
    return startDate;
}

/** 规范化结果：校正后的起止日与需提示的错误类型 */
export type NormalizedCustomDateRange = {
    /** 校正后的开始日 */
    start: Date;
    /** 校正后的结束日 */
    end: Date;
    /** 结束早于开始 */
    orderInvalid: boolean;
    /** 跨度超过最大月数（已对结束或开始做 clamp） */
    spanExceeded: boolean;
};

/**
 * 将自定义起止日规范为 start ≤ end（按日历日）；若开始日晚于结束日则对调二者。
 * 与 App `normalizeCustomDateRangeOrder` 一致：仅校正顺序，不校验跨度、不 clamp。
 * @param startDate - 用户选中的开始日
 * @param endDate - 用户选中的结束日
 * @returns 规范后的 [开始日, 结束日]
 */
export function normalizeCustomDateRangeOrder(
    startDate: Date,
    endDate: Date
): [Date, Date] {
    const startDay = moment(startDate).startOf('day');
    const endDay = moment(endDate).startOf('day');
    if (endDay.isBefore(startDay, 'day')) {
        return [endDate, startDate];
    }
    return [startDate, endDate];
}

/**
 * 将自定义起止日校正到合法区间，供需要自动 clamp 的场景使用（PeriodDateFilter 已改为选完校验、不 clamp）。
 * @param startDate - 用户选择的开始日
 * @param endDate - 用户选择的结束日
 */
export function normalizeCustomDateRangeForPicker(
    startDate: Date,
    endDate: Date
): NormalizedCustomDateRange {
    const startDay = moment(startDate).startOf('day');
    const endDay = moment(endDate).startOf('day');

    if (endDay.isBefore(startDay, 'day')) {
        return {
            start: startDate,
            end: copyTime(startDate, endDate),
            orderInvalid: true,
            spanExceeded: false,
        };
    }

    const maxEndDay = startDay.clone().add(CUSTOM_DATE_RANGE_MAX_MONTHS, 'months');
    if (endDay.isAfter(maxEndDay, 'day')) {
        const clampedEnd = copyTime(maxEndDay.toDate(), endDate);
        return {
            start: startDate,
            end: clampedEnd,
            orderInvalid: false,
            spanExceeded: true,
        };
    }

    const minStartDay = endDay.clone().subtract(CUSTOM_DATE_RANGE_MAX_MONTHS, 'months');
    if (startDay.isBefore(minStartDay, 'day')) {
        const clampedStart = copyTime(minStartDay.toDate(), startDate);
        return {
            start: clampedStart,
            end: endDate,
            orderInvalid: false,
            spanExceeded: true,
        };
    }

    return {
        start: startDate,
        end: endDate,
        orderInvalid: false,
        spanExceeded: false,
    };
}

/**
 * 将 source 的时分秒复制到 target 的日历日上。
 * @param targetDay - 目标日历日
 * @param source - 提供时分的源日期
 */
function copyTime(targetDay: Date, source: Date): Date {
    const next = new Date(targetDay);
    next.setHours(source.getHours(), source.getMinutes(), source.getSeconds(), 0);
    return next;
}
